import React, { useMemo } from "react";

export default function ContactMessageCard({ data }) {
  const fullName = `${data?.FirstName ?? ""} ${data?.LastName ?? ""}`.trim();

  const created = useMemo(() => {
    const raw = data?.CreatedDate;
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d) ? null : d;
  }, [data?.CreatedDate]);

  const localDate = useMemo(() => {
    if (!created) return "—";
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(created);
  }, [created]);

  const timeAgo = useMemo(() => {
    if (!created) return "";
    const diffMs = Date.now() - created.getTime();
    const sec = Math.max(1, Math.floor(diffMs / 1000));
    const min = Math.floor(sec / 60);
    const hr = Math.floor(min / 60);
    const day = Math.floor(hr / 24);
    if (day > 0) return `${day}d ago`;
    if (hr > 0) return `${hr}h ago`;
    if (min > 0) return `${min}m ago`;
    return `${sec}s ago`;
  }, [created]);

  const toast = (msg) => {
    const el = document.createElement("div");
    el.textContent = msg;
    Object.assign(el.style, {
      position: "fixed",
      bottom: "24px",
      left: "50%",
      transform: "translateX(-50%)",
      background: "rgba(0,0,0,0.8)",
      color: "#fff",
      padding: "10px 14px",
      borderRadius: "10px",
      fontSize: "14px",
      zIndex: 9999,
      opacity: "0",
      transition: "opacity 200ms ease",
    });
    document.body.appendChild(el);
    requestAnimationFrame(() => (el.style.opacity = "1"));
    setTimeout(() => {
      el.style.opacity = "0";
      setTimeout(() => el.remove(), 250);
    }, 1300);
  };

  const copy = async (txt, label = "Copied!") => {
    try {
      await navigator.clipboard.writeText(txt ?? "");
      toast(label);
    } catch {
      toast("Copy failed");
    }
  };

  const tel = `tel:${(data?.PhoneNumber || "").replace(/[^\d+]/g, "")}`;

  return (
    <div style={styles.wrap}>
      <header style={styles.header}>
        <div style={styles.titleArea}>
          <div style={styles.avatar} aria-hidden>
            {fullName ? fullName[0].toUpperCase() : "M"}
          </div>
          <div>
            <div style={styles.nameRow}>
              <h2 style={styles.title}>{fullName || "Unknown Sender"}</h2>
            </div>
            <div style={styles.metaRow}>
              <span title={data?.Email} style={styles.meta}>
                📧 {data?.Email || "—"}
              </span>
              <span title={data?.PhoneNumber} style={styles.meta}>
                📞 {data?.PhoneNumber || "—"}
              </span>
              <span style={styles.meta}>📍 {data?.Location || "—"}</span>
            </div>
          </div>
        </div>

        <div style={styles.actionRow}>
          {data?.PhoneNumber && (
            <a href={tel} style={{ ...styles.btn, ...styles.ghostBtn }}>
              Call
            </a>
          )}
          <button
            onClick={() => copy(data?.Email, "Email copied")}
            style={{ ...styles.btn, ...styles.ghostBtn }}
          >
            Copy Email
          </button>
          {data?.PhoneNumber && (
            <button
              onClick={() => copy(data?.PhoneNumber, "Phone copied")}
              style={{ ...styles.btn, ...styles.ghostBtn }}
            >
              Copy Phone
            </button>
          )}
        </div>
      </header>

      <section style={styles.bodyCard}>
        <h3 style={styles.sectionTitle}>Message</h3>
        <p style={styles.bodyText}>{data?.Body || "—"}</p>
      </section>

      {data?.AdditionalNotes ? (
        <section style={styles.noteCard}>
          <h3 style={styles.sectionTitle}>Additional Notes</h3>
          <p style={styles.noteText}>{data.AdditionalNotes}</p>
        </section>
      ) : null}

      <section style={styles.footerRow}>
        <div>
          <div style={styles.timestampLabel}>Received</div>
          <div style={styles.timestampValue}>{localDate}</div>
          {timeAgo && <div style={styles.timestampAgo}>({timeAgo})</div>}
        </div>
        <div style={styles.kvWrap}>
          <div style={styles.kv}>
            <span style={styles.kvKey}>Email:</span>
            <span style={styles.kvVal}>{data?.Email || "—"}</span>
          </div>
          <div style={styles.kv}>
            <span style={styles.kvKey}>Phone:</span>
            <span style={styles.kvVal}>{data?.PhoneNumber || "—"}</span>
          </div>
          <div style={styles.kv}>
            <span style={styles.kvKey}>Location:</span>
            <span style={styles.kvVal}>{data?.Location || "—"}</span>
          </div>
        </div>
      </section>
    </div>
  );
}

const styles = {
  wrap: {
    maxWidth: 880,
    margin: "24px auto",
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
    border: "1px solid #eee",
    overflow: "hidden",
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
    color: "#0f172a",
  },
  header: {
    padding: "20px 22px",
    borderBottom: "1px solid #f1f5f9",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },
  titleArea: { display: "flex", alignItems: "center", gap: 14, minWidth: 280 },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: "linear-gradient(135deg, #e0e7ff, #f5d0fe)",
    display: "grid",
    placeItems: "center",
    fontWeight: 700,
    color: "#111827",
  },
  nameRow: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  title: { margin: 0, fontSize: 18, lineHeight: "24px" },
  badge: {
    fontSize: 12,
    padding: "4px 8px",
    borderRadius: 999,
    fontWeight: 600,
  },
  metaRow: { display: "flex", gap: 12, flexWrap: "wrap", marginTop: 6 },
  meta: { fontSize: 13, color: "#475569" },
  actionRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    width: "100%",
    marginTop: "12px",
    justifyContent: "flex-start",
  },
  btn: {
    border: "1px solid #e2e8f0",
    background: "#fff",
    color: "#0f172a",
    padding: "8px 12px",
    borderRadius: 10,
    fontSize: 14,
    cursor: "pointer",
  },
  iconBtn: {
    border: "1px solid #e2e8f0",
    background: "#fff",
    color: "#0f172a",
    padding: "8px 12px",
    borderRadius: 10,
    fontSize: 14,
    cursor: "pointer",
    fontFamily:
      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  },
  primaryBtn: { background: "#1D4ED8", color: "#fff", borderColor: "#1D4ED8" },
  ghostBtn: { background: "#fff" },
  successBtn: { background: "#059669", color: "#fff", borderColor: "#059669" },
  bodyCard: { padding: "18px 22px", borderBottom: "1px solid #f1f5f9" },
  noteCard: {
    padding: "18px 22px",
    borderBottom: "1px solid #f1f5f9",
    background: "#fafafa",
  },
  sectionTitle: {
    margin: "0 0 8px",
    fontSize: 14,
    color: "#334155",
    letterSpacing: 0.2,
    textTransform: "uppercase",
  },
  bodyText: { margin: 0, fontSize: 15.5, lineHeight: 1.6 },
  noteText: { margin: 0, fontSize: 14.5, lineHeight: 1.55, color: "#475569" },
  footerRow: {
    padding: "16px 22px 22px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },
  timestampLabel: { fontSize: 12, color: "#64748b" },
  timestampValue: { fontSize: 14, fontWeight: 600, marginTop: 2 },
  timestampAgo: { fontSize: 12, color: "#64748b", marginTop: 2 },
  kvWrap: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(160px, 1fr))",
    gap: 10,
    minWidth: 320,
    flex: 1,
  },
  kv: {
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: "10px 12px",
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  kvKey: { fontSize: 12, color: "#64748b" },
  kvVal: {
    fontSize: 14,
    fontWeight: 600,
    color: "#0f172a",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};
